import React, { useEffect, useRef, useState } from "react";
import {
    Button,
    Card,
    Image,
    Input,
    Modal,
    Popconfirm,
    Spin,
    Tag,
    message,
} from "antd";
import { Upload, Trash2 } from "lucide-react";
import dayjs from "dayjs";

// 1. Định nghĩa Interface trực tiếp trong file thay vì lấy từ API
export interface AppointmentPhoto {
    id: number;
    image: string;
    title: string;
    type?: string;
    created_at: string;
}

const TYPE_LABELS: Record<string, string> = {
    treatment: "Điều trị",
    before: "Trước điều trị",
    after: "Sau điều trị",
    invoice: "Hóa đơn",
};

// 2. Tạo dữ liệu mẫu (Mock Data)
const MOCK_PHOTOS: AppointmentPhoto[] = [
    {
        id: 1,
        image: "https://zos.alipayobjects.com/rmsportal/jkjgkEfvpUPVyRjUImniVslZfWPnJuuZ.png",
        title: "Ảnh khám tổng quát",
        type: "before",
        created_at: new Date().toISOString(),
    },
    {
        id: 2,
        image: "https://gw.alipayobjects.com/zos/antfincdn/LlvErxo8H9/photo-1503185912284-5271ff81b9a8.webp",
        title: "Ảnh sau điều trị ngày 1",
        type: "after",
        created_at: new Date(Date.now() - 86400000).toISOString(), // 1 ngày trước
    }
];

interface Props {
    appointmentId?: number;
}

export default function GalleryTab({ appointmentId }: Props) {
    const [photos, setPhotos] = useState<AppointmentPhoto[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Dùng để lưu trữ URL object của ảnh để preview trước khi upload
    const [pendingItems, setPendingItems] = useState<{ file: File; title: string; preview: string }[]>([]);

    const fetchPhotos = () => {
        // Mô phỏng gọi API mất 500ms
        setLoading(true);
        setTimeout(() => {
            setPhotos(MOCK_PHOTOS);
            setLoading(false);
        }, 500);
    };

    useEffect(() => {
        fetchPhotos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointmentId]);

    const clearPending = () => {
        pendingItems.forEach((it) => URL.revokeObjectURL(it.preview));
        setPendingItems([]);
    };

    const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";

        if (!files.length) return;

        const valid: File[] = [];
        for (const f of files) {
            if (!f.type.startsWith("image/")) {
                message.warning(`"${f.name}" không phải file ảnh`);
                continue;
            }
            if (f.size > 10 * 1024 * 1024) {
                message.warning(`"${f.name}" vượt quá 10MB`);
                continue;
            }
            valid.push(f);
        }

        if (!valid.length) return;

        setPendingItems(valid.map((file) => ({
            file,
            title: "",
            preview: URL.createObjectURL(file)
        })));
    };

    const handleUpload = async () => {
        if (!pendingItems.length) return;
        setUploading(true);

        // Mô phỏng quá trình upload mất 1 giây
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Tạo dữ liệu ảnh mới từ các file đã chọn
        const newUploadedPhotos: AppointmentPhoto[] = pendingItems.map((item, index) => ({
            id: Date.now() + index, // Tạo ID ngẫu nhiên
            image: item.preview, // Tái sử dụng URL.createObjectURL làm ảnh hiển thị luôn
            title: item.title.trim() || `Ảnh tải lên ${dayjs().format('HH:mm')}`,
            type: "treatment",
            created_at: new Date().toISOString(),
        }));

        // Thêm ảnh mới vào đầu danh sách hiện tại
        setPhotos(prev => [...newUploadedPhotos, ...prev]);
        setUploading(false);
        clearPending();

        message.success(`Đã tải lên ${newUploadedPhotos.length} ảnh`);
    };

    const handleDelete = async (photoId: number) => {
        // Mô phỏng API xóa mất 500ms
        await new Promise(resolve => setTimeout(resolve, 500));

        setPhotos((prev) => prev.filter((p) => p.id !== photoId));
        message.success("Đã xóa ảnh");
    };

    return (
        <div className="pb-[30px] text-000000e0">
            <div className="flex justify-between items-center mb-5">
                <h2 className="text-lg font-semibold">
                    Hình ảnh điều trị {photos.length > 0 ? `(${photos.length})` : ""}
                </h2>

                <Button
                    icon={<Upload size={16} />}
                    loading={uploading}
                    onClick={() => inputRef.current?.click()}
                >
                    Tải ảnh lên
                </Button>

                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleFiles}
                />
            </div>

            <Spin spinning={loading}>
                {photos.length === 0 && !loading ? (
                    <div className="py-16 text-center text-slate-400 font-medium">
                        Chưa có hình ảnh nào
                    </div>
                ) : (
                    <Image.PreviewGroup>
                        <div className="grid grid-cols-2 gap-4">
                            {photos.map((item) => (
                                <Card
                                    key={item.id}
                                    bodyStyle={{ padding: 10 }}
                                    className="rounded-xl"
                                >
                                    <div className="relative">
                                        {/* Bạn có thể bỏ comment thẻ này nếu muốn hiển thị loại ảnh */}
                                        {/* {item.type && (
                                            <Tag
                                                color="processing"
                                                className="absolute top-3 left-3 z-10"
                                            >
                                                {TYPE_LABELS[item.type] || item.type}
                                            </Tag>
                                        )} */}

                                        <Popconfirm
                                            title="Xóa hình ảnh này?"
                                            okText="Đồng ý"
                                            cancelText="Hủy"
                                            okButtonProps={{ danger: true }}
                                            onConfirm={() => handleDelete(item.id)}
                                        >
                                            <Button
                                                danger
                                                size="small"
                                                shape="circle"
                                                icon={<Trash2 size={13} />}
                                                className="!absolute top-3 right-3 z-10"
                                            />
                                        </Popconfirm>

                                        <Image
                                            src={item.image}
                                            className="rounded-lg object-cover"
                                            height={230}
                                            width="100%"
                                        />
                                    </div>

                                    <div className="text-center mt-3">
                                        <h4 className="font-semibold text-[15px] text-000000e0 truncate px-2" title={item.title}>
                                            {item.title || "—"}
                                        </h4>

                                        <p className="text-slate-400 text-sm">
                                            {item.created_at ? dayjs(item.created_at).format("DD/MM/YYYY HH:mm") : ""}
                                        </p>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </Image.PreviewGroup>
                )}
            </Spin>

            <Modal
                open={pendingItems.length > 0}
                title={`Tải lên ${pendingItems.length} ảnh`}
                okText="Tải lên"
                cancelText="Hủy"
                confirmLoading={uploading}
                onOk={handleUpload}
                onCancel={clearPending}
            >
                <div className="mb-3 text-slate-600 text-sm">
                    Nhập tiêu đề riêng cho từng ảnh (bỏ trống sẽ tự sinh):
                </div>
                <div className="max-h-[360px] overflow-y-auto space-y-3 pr-1">
                    {pendingItems.map((it, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <img
                                src={it.preview}
                                alt={it.file.name}
                                className="w-14 h-14 rounded-lg object-cover border flex-shrink-0"
                            />
                            <Input
                                placeholder={`Tiêu đề ảnh ${i + 1} (VD: Ảnh sau điều trị 3 ngày)`}
                                value={it.title}
                                onChange={(e) =>
                                    setPendingItems((prev) =>
                                        prev.map((p, idx) => (idx === i ? { ...p, title: e.target.value } : p))
                                    )
                                }
                                maxLength={255}
                            />
                        </div>
                    ))}
                </div>
            </Modal>
        </div>
    );
}